import { getSheetSize } from "../domain/sheetSize";
import { getPotentialOffcuts } from "./offcuts";
import {
  buildCutSequenceSummaryDescriptor,
} from "../messages/exportMessages.js";
import { buildCutSimulationCaptionDescriptor } from "../messages/workshopMessages.js";

const MIN_CUT_SEGMENT = 1;
const LABEL_SLOTS = [0.12, 0.42, 0.72, 0.28, 0.58];
const TOLERANCE = 2;
const WORKSHOP_TRIM_THRESHOLD = 100;

function hasUsableArea(node) {
  return node && (node.length > MIN_CUT_SEGMENT || node.width > MIN_CUT_SEGMENT);
}

function rectKey(rect) {
  return [
    Math.round(rect.x),
    Math.round(rect.y),
    Math.round(rect.length),
    Math.round(rect.width),
  ].join(":");
}

function compareRawCuts(left, right) {
  const depthLeft = left.depth ?? 0;
  const depthRight = right.depth ?? 0;
  if (depthLeft !== depthRight) return depthLeft - depthRight;

  const kindOrder = { "strip-rip": 0, "strip-cross": 1 };
  const kindLeft = kindOrder[left.kind] ?? 0;
  const kindRight = kindOrder[right.kind] ?? 0;
  if (kindLeft !== kindRight) return kindLeft - kindRight;

  if (left.axis !== right.axis) return left.axis === "horizontal" ? -1 : 1;
  if (left.position !== right.position) return left.position - right.position;
  return (left.start ?? 0) - (right.start ?? 0);
}

function clampRawCutToBoard(cut, boardLength, boardWidth, margin = 0) {
  const maxX = boardLength + margin;
  const maxY = boardWidth + margin;

  if (cut.axis === "vertical") {
    return {
      ...cut,
      start: Math.max(cut.start, margin),
      end: Math.min(cut.end, maxY),
    };
  }

  return {
    ...cut,
    start: Math.max(cut.start, margin),
    end: Math.min(cut.end, maxX),
  };
}

function splitStock(stock, cut) {
  if (cut.axis === "vertical") {
    const leftLength = cut.position - stock.x;
    const rightLength = stock.x + stock.length - cut.position;
    const pieces = [];
    if (leftLength > TOLERANCE) {
      pieces.push({ x: stock.x, y: stock.y, length: leftLength, width: stock.width });
    }
    if (rightLength > TOLERANCE) {
      pieces.push({ x: cut.position, y: stock.y, length: rightLength, width: stock.width });
    }
    return pieces;
  }

  const topWidth = cut.position - stock.y;
  const bottomWidth = stock.y + stock.width - cut.position;
  const pieces = [];
  if (topWidth > TOLERANCE) {
    pieces.push({ x: stock.x, y: stock.y, length: stock.length, width: topWidth });
  }
  if (bottomWidth > TOLERANCE) {
    pieces.push({ x: stock.x, y: cut.position, length: stock.length, width: bottomWidth });
  }
  return pieces;
}

function findApplicableStock(stocks, cut) {
  return stocks.find((stock) => {
    if (cut.axis === "vertical") {
      if (cut.position <= stock.x + TOLERANCE || cut.position >= stock.x + stock.length - TOLERANCE) {
        return false;
      }

      if (cut.kind === "strip-cross") return false;

      return cut.start >= stock.y - TOLERANCE
        && cut.end <= stock.y + stock.width + TOLERANCE
        && Math.abs((cut.end - cut.start) - stock.width) <= TOLERANCE + 1;
    }

    if (cut.position <= stock.y + TOLERANCE || cut.position >= stock.y + stock.width - TOLERANCE) {
      return false;
    }

    if (cut.kind === "strip-cross") {
      return cut.start >= stock.x - TOLERANCE
        && cut.end <= stock.x + stock.length + TOLERANCE
        && Math.abs(cut.start - stock.x) <= TOLERANCE + 1
        && Math.abs(cut.end - (stock.x + stock.length)) <= TOLERANCE + 1;
    }

    return cut.start >= stock.x - TOLERANCE
      && cut.end <= stock.x + stock.length + TOLERANCE
      && Math.abs((cut.end - cut.start) - stock.length) <= TOLERANCE + 1;
  });
}

function clipCutToStock(cut, stock) {
  if (cut.axis === "vertical") {
    return {
      ...cut,
      start: stock.y,
      end: stock.y + stock.width,
    };
  }

  if (cut.kind === "strip-cross") {
    return {
      ...cut,
      start: stock.x,
      end: stock.x + stock.length,
    };
  }

  return {
    ...cut,
    start: stock.x,
    end: stock.x + stock.length,
  };
}

function findApplicableStockIndex(stocks, cut) {
  return stocks.findIndex((stock) => findApplicableStock([stock], cut) === stock);
}

/**
 * Expose packer-derived raw cuts for tests and debugging.
 */
export function prepareRawCuts(sheet, sourcePanel, strategy, stripsNoCrosscut = false) {
  const { length: boardLength, width: boardWidth } = getSheetSize(sheet, sourcePanel);
  const margin = Number(sheet.margin) || 0;

  if (!sheet.placements?.length) {
    return { boardLength, boardWidth, margin, rawCuts: [] };
  }

  let rawCuts = [];

  if (strategy === "strips") {
    rawCuts = buildStripCutSequence(sheet, sourcePanel, stripsNoCrosscut);
  } else if (sheet.packer?.root) {
    collectCutsFromPacker(sheet.packer.root, margin, rawCuts);
  }

  return {
    boardLength,
    boardWidth,
    margin,
    rawCuts: dedupeExactCuts(
      rawCuts.map((cut) => clampRawCutToBoard(cut, boardLength, boardWidth, margin)),
    ),
  };
}

function createsThinTrim(stock, cut) {
  if (cut.axis === "horizontal") {
    const topWidth = cut.position - stock.y;
    const bottomWidth = stock.y + stock.width - cut.position;
    const trimWidth = Math.min(topWidth, bottomWidth);
    return trimWidth > TOLERANCE && trimWidth < WORKSHOP_TRIM_THRESHOLD;
  }

  const leftLength = cut.position - stock.x;
  const rightLength = stock.x + stock.length - cut.position;
  const trimLength = Math.min(leftLength, rightLength);
  return trimLength > TOLERANCE && trimLength < WORKSHOP_TRIM_THRESHOLD;
}

function createsThinWaste(stock, cut) {
  return createsThinTrim(stock, cut)
    || splitStock(stock, cut).some((piece) => {
      const minSide = Math.min(piece.length, piece.width);
      return minSide < WORKSHOP_TRIM_THRESHOLD;
    });
}

/**
 * Score a candidate cut for workshop-friendly panel-saw order:
 * full rips on large stock first, thin trim cuts last.
 */
function scoreWorkshopCut(stock, rawCut) {
  const cut = clipCutToStock(rawCut, stock);
  const span = cut.end - cut.start;
  const stockArea = stock.length * stock.width;
  const isFullHorizontal = cut.axis === "horizontal"
    && Math.abs(span - stock.length) <= TOLERANCE + 1;
  const isFullVertical = cut.axis === "vertical"
    && Math.abs(span - stock.width) <= TOLERANCE + 1;

  let score = stockArea;

  if (isFullHorizontal) score += 10_000_000;
  else if (isFullVertical) score += 8_000_000;
  else if (cut.axis === "horizontal") score += 500_000;
  else score += 200_000;

  if (createsThinWaste(stock, cut)) score -= 50_000_000;

  score -= stock.y * 100;
  score -= stock.x;

  return score;
}

function selectAppliedIndex(stocks, remaining, applicableIndexes, options) {
  const { primaryOffcut = null, workshopOrdering = false } = options;
  let candidates = applicableIndexes;

  if (primaryOffcut) {
    const nonTouching = candidates.filter(
      (index) => !cutTouchesRect(remaining[index], primaryOffcut),
    );
    if (nonTouching.length) candidates = nonTouching;
  }

  if (workshopOrdering && candidates.length > 1) {
    const minY = Math.min(
      ...candidates.map((index) => findApplicableStock(stocks, remaining[index]).y),
    );
    const topBand = candidates.filter((index) => {
      const stock = findApplicableStock(stocks, remaining[index]);
      return stock.y <= minY + TOLERANCE;
    });
    if (topBand.length) candidates = topBand;

    const withoutThinTrim = candidates.filter((index) => {
      const stock = findApplicableStock(stocks, remaining[index]);
      const cut = clipCutToStock(remaining[index], stock);
      return !createsThinTrim(stock, cut);
    });
    if (withoutThinTrim.length) candidates = withoutThinTrim;
  }

  if (!workshopOrdering || candidates.length === 1) {
    return candidates[0];
  }

  return candidates.reduce((bestIndex, index) => {
    const bestStock = findApplicableStock(stocks, remaining[bestIndex]);
    const candidateStock = findApplicableStock(stocks, remaining[index]);
    const bestScore = scoreWorkshopCut(bestStock, remaining[bestIndex]);
    const candidateScore = scoreWorkshopCut(candidateStock, remaining[index]);

    if (candidateScore !== bestScore) {
      return candidateScore > bestScore ? index : bestIndex;
    }

    return compareRawCuts(remaining[index], remaining[bestIndex]) < 0 ? index : bestIndex;
  });
}

/**
 * Turn raw guillotine intents into a physically executable panel-saw sequence.
 */
export function materializeCutSequence(rawCuts, boardLength, boardWidth, margin = 0, options = {}) {
  const { primaryOffcut = null, workshopOrdering = false } = options;
  const remaining = [...rawCuts].sort(compareRawCuts);
  let stocks = [{ x: margin, y: margin, length: boardLength, width: boardWidth }];
  const result = [];

  while (remaining.length) {
    const currentStocks = stocks;
    const applicableIndexes = remaining
      .map((cut, index) => (findApplicableStock(currentStocks, cut) ? index : -1))
      .filter((index) => index >= 0);

    if (!applicableIndexes.length) break;

    const appliedIndex = selectAppliedIndex(stocks, remaining, applicableIndexes, {
      primaryOffcut,
      workshopOrdering,
    });

    const rawCut = remaining.splice(appliedIndex, 1)[0];
    const stock = findApplicableStock(stocks, rawCut);
    const cut = clipCutToStock(rawCut, stock);
    result.push(cut);

    const stockIndex = findApplicableStockIndex(stocks, rawCut);
    const pieces = splitStock(stock, cut);
    stocks = [
      ...stocks.slice(0, stockIndex),
      ...stocks.slice(stockIndex + 1),
      ...pieces,
    ];
  }

  return result;
}

export function simulatePanelSawCuts(cuts, boardLength, boardWidth, margin = 0) {
  let stocks = [{ x: margin, y: margin, length: boardLength, width: boardWidth }];

  for (let index = 0; index < cuts.length; index += 1) {
    const cut = cuts[index];
    const stock = findApplicableStock(stocks, cut);
    if (!stock) {
      return {
        ok: false,
        failedAt: index,
        cut,
        stocks,
      };
    }

    const stockIndex = stocks.findIndex((item) => rectKey(item) === rectKey(stock));
    const pieces = splitStock(stock, cut);
    stocks = [
      ...stocks.slice(0, stockIndex),
      ...stocks.slice(stockIndex + 1),
      ...pieces,
    ];
  }

  return { ok: true, stocks };
}

function collectCutsFromPacker(node, margin, cuts, depth = 0) {
  if (!node?.used) return;

  const panelLength = node.right ? node.right.x - node.x : node.length;
  const panelWidth = node.down ? node.down.y - node.y : node.width;
  const nodeStartX = node.x + margin;
  const nodeStartY = node.y + margin;
  const nodeEndX = node.x + node.length + margin;
  const nodeEndY = node.y + node.width + margin;

  const horizontalCut = node.down && hasUsableArea(node.down)
    ? {
      axis: "horizontal",
      position: node.y + panelWidth + margin,
      start: nodeStartX,
      end: nodeEndX,
      depth,
    }
    : null;
  const verticalCut = node.right && hasUsableArea(node.right)
    ? {
      axis: "vertical",
      position: node.x + panelLength + margin,
      start: nodeStartY,
      end: horizontalCut ? horizontalCut.position : nodeEndY,
      depth,
    }
    : null;

  if (horizontalCut) cuts.push(horizontalCut);
  if (verticalCut) cuts.push(verticalCut);

  if (node.right) collectCutsFromPacker(node.right, margin, cuts, depth + 1);
  if (node.down) collectCutsFromPacker(node.down, margin, cuts, depth + 1);
}

function mergeStripRipPositions(positions, minGap = 8) {
  if (!positions.length) return [];

  const sorted = [...positions].sort((left, right) => left - right);
  const merged = [sorted[0]];

  sorted.slice(1).forEach((position) => {
    const previous = merged[merged.length - 1];
    if (position - previous < minGap) {
      merged[merged.length - 1] = Math.round((previous + position) / 2);
      return;
    }
    merged.push(position);
  });

  return merged;
}

function buildStripCutSequence(sheet, sourcePanel, stripsNoCrosscut) {
  const { length: boardLength, width: boardWidth } = getSheetSize(sheet, sourcePanel);
  const placements = sheet.placements || [];
  if (!placements.length) return [];

  const kerf = Math.max(Number(sheet.kerf) || 2.2, 0);
  const minRipGap = Math.max(kerf + 2, 8);

  const widthCounts = placements.reduce((result, placement) => {
    const key = Math.round(placement.width);
    result[key] = (result[key] || 0) + 1;
    return result;
  }, {});

  const cuts = [];

  if (stripsNoCrosscut) {
    placements.forEach((placement) => {
      const repeatedWidth = widthCounts[Math.round(placement.width)] > 1;
      if (repeatedWidth) {
        [placement.y, placement.y + placement.width].forEach((position) => {
          if (position > 0 && position < boardWidth) {
            cuts.push({
              axis: "horizontal",
              position,
              start: 0,
              end: boardLength,
              kind: "strip-rip",
            });
          }
        });
      } else {
        [placement.x, placement.x + placement.length].forEach((position) => {
          if (position > 0 && position < boardLength) {
            cuts.push({
              axis: "vertical",
              position,
              start: 0,
              end: boardWidth,
              kind: "strip-rip",
            });
          }
        });
      }
    });
    return cuts;
  }

  const xPositions = mergeStripRipPositions(
    [...new Set(
      placements.flatMap((placement) => [placement.x, placement.x + placement.length]),
    )]
      .map(Math.round)
      .filter((position) => position > 0 && position < boardLength),
    minRipGap,
  );

  xPositions.forEach((position) => {
    cuts.push({
      axis: "vertical",
      position,
      start: 0,
      end: boardWidth,
      kind: "strip-rip",
    });
  });

  placements.forEach((placement) => {
    [placement.y, placement.y + placement.width].forEach((position) => {
      if (position <= 0 || position >= boardWidth) return;
      cuts.push({
        axis: "horizontal",
        position,
        start: placement.x,
        end: placement.x + placement.length,
        kind: "strip-cross",
      });
    });
  });

  return cuts;
}

function cutTouchesRect(cut, rect) {
  if (cut.axis === "vertical") {
    const onEdge = Math.abs(cut.position - rect.x) <= TOLERANCE
      || Math.abs(cut.position - (rect.x + rect.length)) <= TOLERANCE;
    return onEdge && cut.end > rect.y + TOLERANCE && cut.start < rect.y + rect.width - TOLERANCE;
  }

  const onEdge = Math.abs(cut.position - rect.y) <= TOLERANCE
    || Math.abs(cut.position - (rect.y + rect.width)) <= TOLERANCE;
  return onEdge && cut.end > rect.x + TOLERANCE && cut.start < rect.x + rect.length - TOLERANCE;
}

function dedupeExactCuts(cuts) {
  const seen = new Set();

  return cuts.filter((cut) => {
    const key = [
      cut.axis,
      Math.round(cut.position),
      Math.round(cut.start),
      Math.round(cut.end),
    ].join(":");

    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function filterCutsToBoard(cuts, boardLength, boardWidth) {
  return cuts.filter((cut) => {
    if (cut.axis === "vertical") {
      return cut.position > 0 && cut.position < boardLength && cut.end > cut.start;
    }
    return cut.position > 0 && cut.position < boardWidth && cut.end > cut.start;
  });
}

function staggerCutLabels(cuts) {
  const groups = new Map();

  cuts.forEach((cut, index) => {
    const bucket = Math.round(cut.position / 24) * 24;
    const key = `${cut.axis}:${bucket}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(index);
  });

  groups.forEach((indices) => {
    indices.forEach((cutIndex, slotIndex) => {
      const cut = cuts[cutIndex];
      const span = cut.end - cut.start;
      const slot = LABEL_SLOTS[slotIndex % LABEL_SLOTS.length];
      cut.labelPos = cut.start + span * slot;
    });
  });

  return cuts;
}

function numberCuts(cuts) {
  return cuts.map((cut, index) => ({
    ...cut,
    number: index + 1,
  }));
}

/**
 * Build an ordered guillotine cut sequence for export visuals.
 * Strategy affects cut order and which cuts are shown, not panel placement.
 */
export function buildCutSequence(sheet, sourcePanel, {
  strategy = "efficient_cutting",
  stripsNoCrosscut = strategy === "strips",
  minOffcutLength = 500,
  minOffcutWidth = 300,
} = {}) {
  const prepared = prepareRawCuts(sheet, sourcePanel, strategy, stripsNoCrosscut);
  const { boardLength, boardWidth, margin, rawCuts: preparedCuts } = prepared;

  if (!preparedCuts.length) {
    return [];
  }

  const materializeOptions = {
    workshopOrdering: strategy === "efficient_cutting" || strategy === "preserve_offcuts",
  };

  if (strategy === "preserve_offcuts") {
    const offcuts = getPotentialOffcuts(
      sheet,
      sourcePanel,
      minOffcutLength,
      minOffcutWidth,
      "merged",
    );
    materializeOptions.primaryOffcut = offcuts[0] || null;
  }

  const cuts = materializeCutSequence(
    preparedCuts,
    boardLength,
    boardWidth,
    margin,
    materializeOptions,
  );

  return staggerCutLabels(numberCuts(filterCutsToBoard(cuts, boardLength, boardWidth)));
}

export function getCutSequenceSummary(cuts, strategy) {
  return buildCutSequenceSummaryDescriptor(cuts, strategy);
}

export function getCutSimulationCaption(cut, boardLength, boardWidth) {
  return buildCutSimulationCaptionDescriptor(cut, boardLength, boardWidth);
}
